import { useState } from 'react';
import { Search, Plus, Building2, Filter } from 'lucide-react';
import { useOrganizations, type OrgFilters } from '../hooks/useOrganizations';
import { Badge } from '../components/shared/Badge';
import { StatusDot } from '../components/shared/StatusDot';
import { EmptyState } from '../components/shared/EmptyState';
import { cn } from '../lib/cn';

interface OrganizationsViewProps {
  onNavigate: (view: string, params?: Record<string, string>) => void;
}

const typeOptions = ['', 'agency', 'carrier', 'mga', 'wholesaler', 'tpa', 'vendor', 'client', 'other'];
const verticalOptions = ['', 'commercial', 'personal', 'benefits', 'surety', 'specialty', 'mixed'];

export function OrganizationsView({ onNavigate }: OrganizationsViewProps) {
  const [filters, setFilters] = useState<OrgFilters>({ limit: 50, offset: 0 });
  const [searchText, setSearchText] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const { data, isLoading, error } = useOrganizations({
    ...filters,
    search: searchText || undefined,
  });

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setFilters((f) => ({ ...f, offset: 0, search: searchText }));
  }

  function handleRowClick(orgId: string) {
    onNavigate('org-detail', { orgId });
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Organizations</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            {data ? `${data.total} organizations` : 'Manage your organizations'}
          </p>
        </div>
        <button className="btn-primary" onClick={() => onNavigate('org-detail', { orgId: 'new' })}>
          <Plus className="h-4 w-4" />
          New Organization
        </button>
      </div>

      {/* Filters */}
      <div className="border-b border-surface-200 bg-white px-6 py-3">
        <div className="flex items-center gap-3">
          <form onSubmit={handleSearch} className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search organizations..."
              className="input pl-9"
            />
          </form>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className={cn('btn-secondary btn-sm', showFilters && 'bg-brand-50 border-brand-200 text-brand-700')}
          >
            <Filter className="h-3.5 w-3.5" />
            Filters
          </button>
        </div>

        {showFilters && (
          <div className="mt-3 flex items-center gap-3">
            <div>
              <label className="label mb-1">Type</label>
              <select
                className="input py-1.5"
                value={filters.type ?? ''}
                onChange={(e) => setFilters((f) => ({ ...f, type: e.target.value || undefined, offset: 0 }))}
              >
                <option value="">All Types</option>
                {typeOptions.filter(Boolean).map((t) => (
                  <option key={t} value={t}>{t.toUpperCase()}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label mb-1">Vertical</label>
              <select
                className="input py-1.5"
                value={filters.vertical ?? ''}
                onChange={(e) => setFilters((f) => ({ ...f, vertical: e.target.value || undefined, offset: 0 }))}
              >
                <option value="">All Verticals</option>
                {verticalOptions.filter(Boolean).map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="px-6 py-4">
        {error ? (
          <div className="card p-8 text-center text-sm text-red-600">
            Failed to load organizations. Please try again.
          </div>
        ) : isLoading ? (
          <div className="card divide-y divide-surface-100">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 px-4 py-3">
                <div className="h-8 w-8 animate-pulse rounded bg-surface-200" />
                <div className="flex-1">
                  <div className="h-4 w-48 animate-pulse rounded bg-surface-200" />
                  <div className="mt-1 h-3 w-24 animate-pulse rounded bg-surface-100" />
                </div>
              </div>
            ))}
          </div>
        ) : data && data.organizations.length > 0 ? (
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-header">Name</th>
                  <th className="table-header">Type</th>
                  <th className="table-header">Vertical</th>
                  <th className="table-header">Contacts</th>
                  <th className="table-header">Last Activity</th>
                  <th className="table-header">Tags</th>
                </tr>
              </thead>
              <tbody>
                {data.organizations.map((org) => (
                  <tr
                    key={org.id}
                    onClick={() => handleRowClick(org.id)}
                    className="table-row-hover"
                  >
                    <td className="table-cell">
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded bg-purple-100 text-xs font-semibold text-purple-700">
                          {org.name?.[0]?.toUpperCase()}
                        </div>
                        <div>
                          <div className="font-medium text-slate-900">{org.name}</div>
                          {org.website && (
                            <div className="text-xs text-slate-500">{org.website}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="table-cell">
                      {org.type ? (
                        <Badge color="purple">{org.type}</Badge>
                      ) : (
                        <span className="text-slate-400">--</span>
                      )}
                    </td>
                    <td className="table-cell">
                      {org.vertical ? (
                        <Badge color="indigo">{org.vertical}</Badge>
                      ) : (
                        <span className="text-slate-400">--</span>
                      )}
                    </td>
                    <td className="table-cell text-slate-600">
                      {org.contactCount ?? 0}
                    </td>
                    <td className="table-cell text-xs text-slate-500">
                      {org.lastActivityAt
                        ? new Date(org.lastActivityAt).toLocaleDateString()
                        : '--'}
                    </td>
                    <td className="table-cell">
                      <div className="flex flex-wrap gap-1">
                        {org.tags?.slice(0, 3).map((tag) => (
                          <Badge key={tag} color="slate" size="sm">{tag}</Badge>
                        ))}
                        {org.tags && org.tags.length > 3 && (
                          <Badge color="gray" size="sm">+{org.tags.length - 3}</Badge>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {data.total > data.limit && (
              <div className="flex items-center justify-between border-t border-surface-200 px-4 py-3">
                <span className="text-xs text-slate-500">
                  Showing {data.offset + 1}-{Math.min(data.offset + data.limit, data.total)} of {data.total}
                </span>
                <div className="flex gap-2">
                  <button
                    className="btn-secondary btn-sm"
                    disabled={data.offset === 0}
                    onClick={() => setFilters((f) => ({ ...f, offset: Math.max(0, (f.offset ?? 0) - (f.limit ?? 50)) }))}
                  >
                    Previous
                  </button>
                  <button
                    className="btn-secondary btn-sm"
                    disabled={data.offset + data.limit >= data.total}
                    onClick={() => setFilters((f) => ({ ...f, offset: (f.offset ?? 0) + (f.limit ?? 50) }))}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <EmptyState
            icon={Building2}
            title="No organizations yet"
            description="Get started by adding your first organization or importing from a file."
            actionLabel="Add Organization"
            onAction={() => onNavigate('org-detail', { orgId: 'new' })}
          />
        )}
      </div>
    </div>
  );
}
